import { WORKER_URL, PROXY_URL, YOUTUBE_KEY } from "./constants";
import { extractJSON } from "./utils";

function decorateError(res, data) {
  const err = new Error(data?.error || `Worker ${res.status}`);
  if (res.status === 429) err.code = data?.error === "daily_cap" ? "daily_cap" : "rate_limit";
  if (data?.limit) err.limit = data.limit;
  return err;
}

async function postWorker(path, body) {
  const res = await fetch(`${WORKER_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 429 || !res.ok || !data.ok) throw decorateError(res, data);
  return data;
}

const ANCHOR_CACHE_KEY = "desert_eye_anchor_v1";
const ANCHOR_CACHE_TTL = 48 * 60 * 60 * 1000;

// ── Cache ──
export function loadAnchorCache() {
  try {
    const r = localStorage.getItem(ANCHOR_CACHE_KEY);
    if (!r) return null;
    const { data, ts } = JSON.parse(r);
    return Date.now() - ts > ANCHOR_CACHE_TTL ? null : data;
  } catch { return null; }
}

export function saveAnchorCache(d) {
  try { localStorage.setItem(ANCHOR_CACHE_KEY, JSON.stringify({ data: d, ts: Date.now() })); } catch {}
}

export function loadAnchorCacheWithTs() {
  try {
    const r = localStorage.getItem(ANCHOR_CACHE_KEY);
    if (!r) return null;
    const { data, ts } = JSON.parse(r);
    if (Date.now() - ts > ANCHOR_CACHE_TTL) return null;
    return { data, ts };
  } catch { return null; }
}

// ── Korean RSS ──
const RSS_FEEDS = [
  { name: "한겨레", url: "https://www.hani.co.kr/rss/international" },
  { name: "동아일보", url: "https://rss.donga.com/international.xml" },
  { name: "연합뉴스", url: "https://www.yna.co.kr/rss/international.xml" },
];

const MIDEAST_KEYWORDS = [
  "이란", "iran", "중동", "middle east", "이스라엘", "israel", "미국", "trump", "트럼프",
  "핵", "nuclear", "제재", "sanction", "houthi", "후티", "헤즈볼라", "hezbollah",
  "하마스", "hamas", "가자", "gaza", "시리아", "syria", "레바논", "lebanon",
  "테헤란", "tehran", "예루살렘", "jerusalem", "네타냐후", "netanyahu",
];

async function fetchRSS(feedUrl) {
  const rssProxy = PROXY_URL
    ? `${PROXY_URL}/api/rss?url=${encodeURIComponent(feedUrl)}`
    : `${WORKER_URL}/rss?url=${encodeURIComponent(feedUrl)}`;
  const res = await fetch(rssProxy);
  if (!res.ok) throw new Error(`RSS fetch failed: ${res.status}`);
  const text = await res.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, "text/xml");
  const items = [...doc.querySelectorAll("item")];
  return items.map(item => ({
    title: item.querySelector("title")?.textContent || "",
    link: item.querySelector("link")?.textContent || "",
    pubDate: item.querySelector("pubDate")?.textContent || "",
    description: item.querySelector("description")?.textContent || "",
  }));
}

function filterMideast(articles) {
  return articles.filter(a => {
    const text = `${a.title} ${a.description}`.toLowerCase();
    return MIDEAST_KEYWORDS.some(kw => text.includes(kw.toLowerCase()));
  });
}

export async function fetchKoreanRSS() {
  const results = [];
  const statuses = [];
  for (const feed of RSS_FEEDS) {
    try {
      const items = await fetchRSS(feed.url);
      const filtered = filterMideast(items);
      results.push(...filtered.map(a => ({ ...a, source: feed.name })));
      statuses.push({ source: feed.name, status: "success", count: filtered.length });
    } catch (e) {
      statuses.push({ source: feed.name, status: "fail", error: e.message });
    }
  }
  return { articles: results.slice(0, 15), statuses };
}

// ── YouTube ──
const YT_CHANNELS = {
  jtbc: { playlistId: "PL3Eb1N33oAXjgBJ1yJSdPWfnb6owctEXW", name: "이가혁라이브" },
  newsfactory: { playlistId: "PLpDZdhM6kelSlKqvKUwMGwbZj1OynPDMR", name: "뉴스공장" },
};

async function analyzeYouTubeVideo(videoUrl, instructions, title = null, mode = "analyze") {
  const data = await postWorker("/anchor/youtube/analyze", { videoUrl, mode, instructions, title });
  return data.text;
}

async function searchYouTubeChannelGemini(handle, titleFilter) {
  return { found: false, reason: "fallback_disabled" };
}

async function searchYouTubeChannelAPI(handle, titleFilter) {
  if (!YOUTUBE_KEY) return searchYouTubeChannelGemini(handle, titleFilter);
  try {
    const handleName = handle.replace("@", "");
    const chRes = await fetch(
      `https://www.googleapis.com/youtube/v3/channels?part=id&forHandle=${handleName}&key=${YOUTUBE_KEY}`
    );
    if (!chRes.ok) {
      if (chRes.status === 403) return searchYouTubeChannelGemini(handle, titleFilter);
      return { found: false, reason: `Channel lookup failed: ${chRes.status}` };
    }
    const chData = await chRes.json();
    const channelId = chData.items?.[0]?.id;
    if (!channelId) return { found: false, reason: "Channel not found" };

    const q = titleFilter ? `&q=${encodeURIComponent(titleFilter)}` : "";
    const srRes = await fetch(
      `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${channelId}&order=date&maxResults=5&type=video${q}&key=${YOUTUBE_KEY}`
    );
    if (!srRes.ok) return { found: false, reason: `Search failed: ${srRes.status}` };
    const srData = await srRes.json();

    const items = srData.items || [];
    if (items.length === 0) return { found: false, reason: "No videos found", candidates: [] };

    const filtered = titleFilter
      ? items.filter(v => v.snippet.title.includes(titleFilter))
      : items;
    const candidates = (filtered.length > 0 ? filtered : items).map(v => ({
      url: `https://www.youtube.com/watch?v=${v.id.videoId}`,
      title: v.snippet.title,
      publishedAt: v.snippet.publishedAt,
    }));

    return { found: true, ...candidates[0], candidates };
  } catch (e) {
    return { found: false, reason: e.message };
  }
}

async function fetchPlaylistLatest(playlistId, maxResults = 10) {
  if (!YOUTUBE_KEY) return { found: false, reason: "YouTube API key not set", candidates: [] };
  try {
    const res = await fetch(
      `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&playlistId=${playlistId}&maxResults=${maxResults}&key=${YOUTUBE_KEY}`
    );
    if (!res.ok) return { found: false, reason: `Playlist fetch failed: ${res.status}`, candidates: [] };
    const data = await res.json();
    const items = data.items || [];
    if (items.length === 0) return { found: false, reason: "재생목록 비어 있음", candidates: [] };
    const candidates = items.map(item => ({
      url: `https://www.youtube.com/watch?v=${item.snippet.resourceId.videoId}`,
      title: item.snippet.title,
      publishedAt: item.snippet.publishedAt,
    }));
    return { found: true, ...candidates[0], candidates };
  } catch (e) {
    return { found: false, reason: e.message, candidates: [] };
  }
}

async function processChannel(key, ch) {
  let search = ch.playlistId
    ? await fetchPlaylistLatest(ch.playlistId)
    : await searchYouTubeChannelAPI(ch.handle, ch.filter);
  if (!search.found) {
    return { status: { source: ch.name, status: "skip", reason: search.reason || "영상 없음" }, video: null };
  }

  // For JTBC: only use videos within 3 days, must be relevant to international news
  if (key === "jtbc") {
    const checkPrompt = "이 영상의 제목과 초반 내용을 확인해서 중동/이란/미국 국제 뉴스와 관련 있는지 판단하라.";
    const cutoff = Date.now() - 3 * 24 * 60 * 60 * 1000;
    const allCandidates = search.candidates || [{ url: search.url, title: search.title, publishedAt: search.publishedAt }];
    const recentCandidates = allCandidates.filter(c =>
      !c.publishedAt || new Date(c.publishedAt).getTime() > cutoff
    );
    if (recentCandidates.length === 0) {
      return { status: { source: ch.name, status: "skip", reason: "3일 이내 영상 없음" }, video: null };
    }
    let found = null;
    for (const candidate of recentCandidates) {
      const checkText = await analyzeYouTubeVideo(candidate.url, checkPrompt, candidate.title, "check");
      try {
        const checkResult = JSON.parse(extractJSON(checkText));
        if (checkResult.relevant) { found = candidate; break; }
      } catch {}
    }
    if (!found) {
      return { status: { source: ch.name, status: "skip", reason: "3일 이내 — 국제뉴스 없음" }, video: null };
    }
    search = { ...search, url: found.url, title: found.title };
  }

  // For 뉴스공장: only use 겸손NSC episodes (Iran/war coverage), most recent
  if (key === "newsfactory") {
    const allCandidates = search.candidates || [{ url: search.url, title: search.title, publishedAt: search.publishedAt }];
    const filtered = allCandidates.filter(c => c.title.includes("겸손NSC"));
    if (filtered.length === 0) {
      return { status: { source: ch.name, status: "skip", reason: "겸손NSC 에피소드 없음" }, video: null };
    }
    // Sort by publishedAt descending, take most recent
    filtered.sort((a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0));
    search = { ...search, url: filtered[0].url, title: filtered[0].title };
  }

  const analysisPrompt = key === "newsfactory"
    ? "이 영상에서 중동/이란/미국 관련 부분만 필터링해서 분석하라. 관련 없는 부분은 무시하라."
    : "이 영상을 전체 분석하라. 핵심 주장, 예측, 분석 포인트를 추출하라.";

  const analysisText = await analyzeYouTubeVideo(search.url, analysisPrompt);
  try {
    const analysis = JSON.parse(extractJSON(analysisText));
    return {
      status: { source: ch.name, status: "success" },
      video: { source: ch.name, title: search.title, url: search.url, ...analysis },
    };
  } catch {
    return {
      status: { source: ch.name, status: "success" },
      video: { source: ch.name, title: search.title, url: search.url, summary: analysisText.slice(0, 500) },
    };
  }
}

export async function fetchYouTubeSources() {
  const entries = Object.entries(YT_CHANNELS);
  const settled = await Promise.allSettled(entries.map(([key, ch]) => processChannel(key, ch)));

  const results = [];
  const statuses = [];
  for (const r of settled) {
    if (r.status === "fulfilled") {
      statuses.push(r.value.status);
      if (r.value.video) results.push(r.value.video);
    } else {
      statuses.push({ source: "YouTube", status: "fail", error: r.reason?.message });
    }
  }
  return { videos: results, statuses };
}

// ── Briefing Generation ──
export async function generateBriefing({ foreignNews, koreanArticles, youtubeVideos, scenarios, directionKeyword }) {
  const data = await postWorker("/anchor/briefing/build", {
    foreignNews,
    koreanArticles,
    youtubeVideos,
    scenarios: scenarios || [],
    directionKeyword,
  });
  return data.briefing;
}

export async function fetchPreBriefing() {
  try {
    const res = await fetch(`${WORKER_URL}/anchor/briefing`);
    if (res.ok) {
      const data = await res.json();
      if (data.ok && data.briefing) return { briefing: data.briefing, ts: data.ts };
    }
  } catch {}
  return null;
}

export async function generateSubQuestions(question, intent) {
  const data = await postWorker("/anchor/sub-questions", { question, intent: intent || "" });
  return data.items;
}
